import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// The session token lives in localStorage so every request can read it
// synchronously. On iOS, though, WKWebView localStorage is not durable — the OS
// can purge it (storage pressure, app updates), which silently signs the reader
// out. In the native app we mirror the token into Capacitor Preferences
// (UserDefaults) and restore it from there at launch.
const KEY = 'token';

const isNative = () => Capacitor.isNativePlatform();

export function getToken() {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    localStorage.setItem(KEY, token);
  } catch {
    /* ignore quota errors; the native mirror below still persists it */
  }
  if (isNative()) Preferences.set({ key: KEY, value: token }).catch(() => {});
}

export function clearToken() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  if (isNative()) Preferences.remove({ key: KEY }).catch(() => {});
}

// Call once before the app renders. Copies the native-persisted token back into
// localStorage if the WebView lost it. A no-op on the web.
export async function restoreToken() {
  if (!isNative() || getToken()) return;
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (value) localStorage.setItem(KEY, value);
  } catch {
    /* nothing to restore */
  }
}

// Seconds since the token was issued, or null if it can't be read (legacy
// tokens have no "iat").
export function tokenAgeSeconds(token = getToken()) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.iat !== 'number') return null;
    return Date.now() / 1000 - payload.iat;
  } catch {
    return null;
  }
}
